import { cn } from '@/lib/infra/utils';

/**
 * TrendArrow — the consensus trend arrow (behavior B of the consensus
 * trend engine, first specified for HYDRA_API "Unabated consensus update").
 * Used by the OddsPapi values: the dashboard OP/PNCL column and the trade
 * panel's O row (its Unabated users retired with that channel, #1192).
 *
 * Lifecycle per move (~20s total, timed by the engine's direction read):
 * the up/down tone burns bright on a fresh move and drains to almost
 * fully grey over the 10s trend window (strength ≈ exp(−age/3.5s) ≈ 0.06
 * at 10s), the grey arrow then rests for a further ~10s linger, and the
 * engine reads direction 0 after that — rendered here as an INVISIBLE
 * spacer, never an unmount, so the slot stays reserved and the number
 * never shifts when the arrow appears or hides.
 */
export function TrendArrow({
  direction,
  strength,
  sizeClass,
  className,
}: {
  /** −1 down / +1 up / 0 = never moved OR the ~20s lifetime elapsed
   *  (renders the invisible spacer). */
  direction: -1 | 0 | 1;
  /** 0..1 recency-weighted delta mass — the tone's share over the grey base. */
  strength: number;
  sizeClass?: string;
  className?: string;
}) {
  const glyph = direction < 0 ? '▼' : '▲';
  return (
    <span
      aria-hidden
      className={cn(
        'relative inline-block leading-none',
        sizeClass,
        className,
        direction === 0 && 'invisible',
      )}
    >
      <span className="text-muted-foreground/60">{glyph}</span>
      <span
        className={cn(
          'absolute inset-0 transition-opacity duration-200 ease-linear',
          direction < 0 ? 'text-negative' : 'text-positive',
        )}
        style={{ opacity: Math.max(0, Math.min(1, strength)) }}
      >
        {glyph}
      </span>
    </span>
  );
}
